import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
    Check, X, Star, Shield, Zap, Crown, ArrowRight, BadgeCheck,
    Phone, Mail, MapPin, Building2, User, Briefcase, Globe,
    Camera, BarChart3, Headphones, Sparkles, ChevronDown, ChevronUp,
    Award, Eye, TrendingUp, MessageSquare, Send, ArrowDown,
    Clock, Users, Target, Handshake, CircleDollarSign,
    Wrench, Search, ThumbsUp, FileCheck, Paintbrush, Hammer,
    Droplets, KeyRound, Ruler, Truck, TreePine, Thermometer,
    DoorOpen, Scale, Landmark, CookingPot, Grid3x3, Home,
    ClipboardCheck, Palette, Lock, ChevronRight, Play
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { HomlyLogo } from '../../components/brand/HomlyLogo';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ ANIMATION HELPERS ============
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function AnimatedSection({ children, className = '', delay = 0 }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-60px" });
    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeUp}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// ============ DATA ============
const PLANS = {
    partenaire: {
        name: 'Partenaire', price: 29, tagline: 'Tout inclus, sans engagement', icon: Crown,
        popular: true, color: 'from-[#7C3AED] to-[#F59E0B]', borderColor: 'border-[#F59E0B]/50',
        features: [
            { text: 'Profil professionnel complet', included: true },
            { text: "Visible dans l'annuaire Homly Partenaires", included: true },
            { text: 'Badge "Vérifié Homly"', included: true },
            { text: "Zone d'intervention personnalisable", included: true },
            { text: 'Avis illimités', included: true, highlight: true },
            { text: 'Contact email + téléphone direct', included: true, highlight: true },
            { text: 'Mise en avant dans les résultats', included: true, highlight: true },
            { text: 'Galerie photos (jusqu\'à 20)', included: true, highlight: true },
            { text: 'Recommandations prioritaires aux acheteurs', included: true, highlight: true },
            { text: 'Statistiques de profil (vues, clics)', included: true, highlight: true },
            { text: 'Support prioritaire 7j/7', included: true, highlight: true },
        ],
    },
};

const SPECIALTIES_DISPLAY = [
    { label: 'Plombier', icon: Droplets }, { label: 'Électricien', icon: Zap },
    { label: 'Peintre', icon: Paintbrush }, { label: 'Maçon', icon: Hammer },
    { label: 'Carreleur', icon: Grid3x3 }, { label: 'Couvreur', icon: Home },
    { label: 'Serrurier', icon: KeyRound }, { label: 'Diagnostiqueur', icon: ClipboardCheck },
    { label: 'Architecte', icon: Ruler }, { label: 'Décorateur', icon: Palette },
    { label: 'Déménageur', icon: Truck }, { label: 'Paysagiste', icon: TreePine },
    { label: 'Cuisiniste', icon: CookingPot }, { label: 'Chauffagiste', icon: Thermometer },
    { label: 'Menuisier', icon: DoorOpen }, { label: 'Notaire', icon: Scale },
    { label: 'Courtier', icon: Landmark }, { label: 'Nettoyage', icon: Sparkles },
    { label: 'Entreprise générale', icon: Building2 },
];

const TESTIMONIALS = [
    {
        name: 'Sophie M.', role: 'Artisan plombier — bêta testeur', rating: 5,
        text: "Je teste Homly Partenaires depuis quelques semaines. Le concept est simple : au lieu de démarcher les agences une par une, mon profil est visible directement dans leur outil de travail. J'attends les premiers retours clients.",
        metric: 'Bêta testeur actif',
    },
    {
        name: 'Thomas R.', role: 'Peintre décorateur — bêta testeur', rating: 4,
        text: "L'idée de recevoir des recommandations d'agents immobiliers plutôt que de faire de la pub sur Google me parle. Le profil est bien fait, la vérification donne confiance. On verra les résultats quand le réseau grandira.",
        metric: 'Profil vérifié',
    },
    {
        name: 'Claire D.', role: 'Architecte d\'intérieur — bêta testeur', rating: 5,
        text: "J'ai déjà travaillé avec des agents immobiliers pour du home staging, mais c'était toujours du bouche-à-oreille informel. Avec Homly Partenaires, c'est structuré et professionnel. Je recommande à mes confrères de s'inscrire tôt.",
        metric: 'Inscrite dès le lancement',
    },
];

const FAQ = [
    { q: "Combien ça coûte concrètement ?", a: "Un seul forfait, tout inclus : 29€/mois HT. Sans engagement : vous pouvez résilier à tout moment, sans frais. Au vu du panier moyen des travaux post-achat (15 000€ selon la FNAIM), un seul client rentabilise votre abonnement pour des années." },
    { q: "Comment les agents immobiliers me trouvent ?", a: "Votre profil apparaît dans l'annuaire Homly Partenaires, intégré à la plateforme utilisée quotidiennement par les agents. Ils peuvent filtrer par spécialité, zone géographique et note. Quand un de leurs clients a besoin de travaux, ils vous recommandent directement." },
    { q: "C'est ouvert uniquement aux agents immobiliers ?", a: "Pour le moment, oui — Homly Partenaires fonctionne via les recommandations d'agents immobiliers. Mais à terme, la plateforme s'ouvrira au grand public : n'importe quel particulier pourra trouver et contacter un artisan vérifié Homly, directement. Les premiers inscrits seront déjà bien positionnés quand cette ouverture arrivera." },
    { q: "En quoi c'est différent des Pages Jaunes ou Google ?", a: "Sur les annuaires classiques, les clients vous comparent à 15 concurrents. Ici, c'est l'agent immobilier qui vous recommande personnellement à son client. La confiance est déjà établie. Le taux de conversion d'une recommandation directe est bien supérieur à une demande de devis anonyme." },
    { q: "C'est une bêta — pourquoi m'inscrire maintenant ?", a: "Parce que les premiers inscrits auront un avantage décisif : profil déjà actif, avis accumulés et ancienneté quand le réseau s'ouvrira au grand public. C'est comme avoir un restaurant bien noté avant l'ouverture de la rue piétonne." },
    { q: "Comment fonctionne la vérification ?", a: "Après votre inscription, notre équipe vérifie votre identité, vos certifications et votre SIRET sous 48h. Une fois validé, vous obtenez le badge 'Vérifié Homly' visible par tous les agents. C'est ce badge qui fait la différence." },
    ];

// ============ SUBCOMPONENTS ============

function CountUp({ target, suffix = '', duration = 2000 }) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    useEffect(() => {
        if (!isInView) return;
        let start = 0;
        const step = Math.ceil(target / (duration / 16));
        const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(start);
        }, 16);
        return () => clearInterval(timer);
    }, [isInView, target, duration]);
    return <span ref={ref}>{count.toLocaleString('fr-FR')}{suffix}</span>;
}

function PricingCard({ plan, planKey, isSelected, onSelect }) {
    const Icon = plan.icon;
    return (
        <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }} className="relative">
            {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 px-4 py-1 text-xs font-bold shadow-lg">
                        Le + populaire
                    </Badge>
                </div>
            )}
            <Card
                data-testid={`plan-card-${planKey}`}
                onClick={() => onSelect(planKey)}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                    isSelected ? `ring-2 ring-[#F59E0B] ${plan.borderColor} bg-card` : 'border-border bg-card hover:border-[#7C3AED]/40'
                } ${plan.popular ? 'scale-[1.02]' : ''}`}
            >
                <div className={`h-1.5 bg-gradient-to-r ${plan.color}`} />
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-foreground">{plan.name}</h3>
                            <p className="text-xs text-muted-foreground">{plan.tagline}</p>
                        </div>
                    </div>
                    <div className="mb-6">
                        <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-extrabold text-foreground">{plan.price}€</span>
                            <span className="text-muted-foreground text-sm">/mois HT</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Sans engagement, résiliable à tout moment</p>
                    </div>
                    <ul className="space-y-2.5">
                        {plan.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                                {f.included ? (
                                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${f.highlight ? 'text-[#F59E0B]' : 'text-green-400'}`} />
                                ) : (
                                    <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-600" />
                                )}
                                <span className={`text-sm ${f.included ? (f.highlight ? 'text-foreground font-medium' : 'text-muted-foreground') : 'text-slate-600 line-through'}`}>
                                    {f.text}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Button className={`w-full mt-6 ${isSelected || plan.popular ? 'bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white hover:opacity-90' : 'bg-muted text-foreground hover:bg-accent'}`}>
                        {isSelected ? 'Sélectionné' : 'Choisir ce forfait'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </Card>
        </motion.div>
    );
}

function RegistrationForm({ selectedPlan, specialties, onSubmit, submitting }) {
    const [form, setForm] = useState({
        name: '', company_name: '', email: '', phone: '', password: '',
        specialty: '', description: '', city: '', postal_code: '',
        department: '', siret: '', website: '', service_radius_km: 30,
    });
    const update = (field, value) => setForm(f => ({ ...f, [field]: value }));
    const handleSubmit = (e) => { e.preventDefault(); onSubmit({ ...form, plan: selectedPlan }); };

    return (
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="space-y-6">
            <Card className="bg-card border-border p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#7C3AED]" /> Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-sm text-muted-foreground mb-1 block">Nom complet *</label><Input data-testid="reg-name" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Jean Dupont" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Nom de l'entreprise *</label><Input data-testid="reg-company" value={form.company_name} onChange={e => update('company_name', e.target.value)} placeholder="Dupont Plomberie" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Email professionnel *</label><Input data-testid="reg-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="contact@entreprise.fr" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Téléphone *</label><Input data-testid="reg-phone" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="06 12 34 56 78" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Mot de passe *</label><Input data-testid="reg-password" type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="Min. 8 caractères" required minLength={8} /></div>
                </div>
            </Card>
            <Card className="bg-card border-border p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#F59E0B]" /> Votre activité
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Spécialité *</label>
                        <select data-testid="reg-specialty" value={form.specialty} onChange={e => update('specialty', e.target.value)} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground" required>
                            <option value="">Choisir une spécialité</option>
                            {specialties.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                    </div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">SIRET</label><Input data-testid="reg-siret" value={form.siret} onChange={e => update('siret', e.target.value)} placeholder="123 456 789 00012" /></div>
                    <div className="md:col-span-2">
                        <label className="text-sm text-muted-foreground mb-1 block">Description de votre activité *</label>
                        <textarea data-testid="reg-description" value={form.description} onChange={e => update('description', e.target.value)} className="w-full bg-muted border border-border rounded-lg px-3 py-3 text-sm text-foreground min-h-[100px]" placeholder="Décrivez votre expertise, vos spécialisations, vos années d'expérience..." required />
                    </div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Site web</label><Input value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://www.monsite.fr" /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Rayon d'intervention (km)</label><Input type="number" value={form.service_radius_km} onChange={e => update('service_radius_km', parseInt(e.target.value) || 30)} min={5} max={200} /></div>
                </div>
            </Card>
            <Card className="bg-card border-border p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#7C3AED]" /> Localisation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="text-sm text-muted-foreground mb-1 block">Ville *</label><Input data-testid="reg-city" value={form.city} onChange={e => update('city', e.target.value)} placeholder="Lyon" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Code postal *</label><Input data-testid="reg-postal" value={form.postal_code} onChange={e => update('postal_code', e.target.value)} placeholder="69003" required /></div>
                    <div><label className="text-sm text-muted-foreground mb-1 block">Département</label><Input value={form.department} onChange={e => update('department', e.target.value)} placeholder="69" /></div>
                </div>
            </Card>
            <div className="flex flex-col items-center gap-3">
                <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white px-8 py-3 text-base font-semibold hover:opacity-90" data-testid="reg-submit">
                    {submitting ? 'Inscription en cours...' : `S'inscrire — Forfait ${PLANS[selectedPlan]?.name} (${PLANS[selectedPlan]?.price}€/mois)`}
                    <Send className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-muted-foreground text-center max-w-md">
                    En vous inscrivant, vous acceptez nos conditions d'utilisation. Votre profil sera activé après vérification sous 48h.
                </p>
            </div>
        </motion.form>
    );
}

// ============ MAIN PAGE ============
export default function ProRegistrationPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'partenaire');
    const [showForm, setShowForm] = useState(false);
    const [specialties, setSpecialties] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showFaq, setShowFaq] = useState(null);
    const pricingRef = useRef(null);
    const formRef = useRef(null);

    useEffect(() => {
        fetch(`${API}/api/professionals/specialties`).then(r => r.json()).then(d => setSpecialties(d.specialties || [])).catch(() => {});
    }, []);

    const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
    const openForm = () => { setShowForm(true); setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); };

    const handleSubmit = async (formData) => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/professionals/register`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (res.ok && data.access_token) {
                localStorage.setItem('pro_token', data.access_token);
                localStorage.setItem('pro_profile', JSON.stringify(data.professional));
                navigate('/pro/dashboard');
            } else {
                alert(data.detail || "Erreur lors de l'inscription");
            }
        } catch (e) { alert("Erreur de connexion"); }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="pro-registration-page">
            {/* ===== HEADER ===== */}
            <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <HomlyLogo size="md" />
                        <Badge className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white border-0 text-[10px]">PRO</Badge>
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link to="/pro/login">
                            <Button variant="ghost" size="sm" className="text-muted-foreground hidden sm:flex">Déjà inscrit ?</Button>
                        </Link>
                        <Button size="sm" onClick={scrollToPricing} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white text-xs" data-testid="header-cta">
                            Voir les tarifs
                        </Button>
                    </div>
                </div>
            </header>

            {/* ===== HERO ===== */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] rounded-full blur-[160px] bg-[#7C3AED]/8" />
                    <div className="absolute bottom-0 right-1/6 w-[400px] h-[400px] rounded-full blur-[140px] bg-[#F59E0B]/6" />
                </div>
                <div className="max-w-5xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 text-center relative z-10">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20 mb-4 py-1.5 px-4">
                            <Handshake className="w-3.5 h-3.5 mr-1.5" /> Le réseau qui vous apporte des clients
                        </Badge>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold ml-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                            Bêta privée — places limitées
                        </div>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 leading-[1.1] tracking-tight font-heading"
                    >
                        Les agents immobiliers<br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">
                            recommandent vos services
                        </span><br />
                        à leurs clients
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                        className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed"
                    >
                        Chaque vente immobilière génère en moyenne <strong className="text-foreground">15 000€ de travaux</strong>.
                        Avec Homly Partenaires, les agents vous envoient directement leurs clients.
                        Fini la prospection. Fini les devis sans suite.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4"
                    >
                        <Button onClick={scrollToPricing} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white px-8 py-3 text-base font-semibold shadow-lg shadow-[#7C3AED]/20" data-testid="hero-cta">
                            Commencer — à partir de 29€/mois
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5" /> Sans engagement, résiliable à tout moment
                        </span>
                    </motion.div>
                </div>
            </section>

            {/* ===== SOCIAL PROOF BAR ===== */}
            <section className="border-y border-border bg-card/50">
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <p className="text-center text-xs text-muted-foreground mb-4 uppercase tracking-wider font-medium">Le marché immobilier en France — l'opportunité</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                        {[
                            { value: 900000, suffix: '', label: "Transactions immobilières / an en France", icon: Home },
                            { value: 70, suffix: '%', label: "Des acheteurs font des travaux après l'achat", icon: Wrench },
                            { value: 15000, suffix: '€', label: "Budget moyen travaux post-achat (source FNAIM)", icon: CircleDollarSign },
                            { value: 30000, suffix: '+', label: "Agences immobilières en France", icon: Users },
                        ].map((s, i) => (
                            <div key={i} className="text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <s.icon className="w-4 h-4 text-[#F59E0B]" />
                                    <span className="text-2xl md:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">
                                        <CountUp target={s.value} suffix={s.suffix} />
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground leading-tight">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== PROBLEM / SOLUTION ===== */}
            <section className="py-20 md:py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-4">
                            Vous êtes un bon artisan.<br />
                            <span className="text-muted-foreground">Le problème, c'est de le faire savoir.</span>
                        </h2>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Before */}
                        <AnimatedSection>
                            <Card className="bg-red-500/5 border-red-500/10 p-6 h-full">
                                <div className="flex items-center gap-2 mb-5">
                                    <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                                        <X className="w-4 h-4 text-red-400" />
                                    </div>
                                    <h3 className="font-bold text-foreground">Sans Homly Partenaires</h3>
                                </div>
                                <ul className="space-y-3.5">
                                    {[
                                        "Vous prospectez au hasard, sans savoir qui a besoin de vous",
                                        "Vous répondez à des appels d'offres avec 10 concurrents",
                                        "Le client compare, négocie, et parfois ne rappelle jamais",
                                        "Votre bouche-à-oreille stagne parce que vos clients ne vous recommandent qu'une fois",
                                        "Vous perdez du temps sur des devis qui ne se concrétisent pas",
                                    ].map((t, i) => (
                                        <li key={i} className="flex items-start gap-2.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400/60 mt-2 flex-shrink-0" />
                                            <span className="text-sm text-muted-foreground">{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </AnimatedSection>

                        {/* After */}
                        <AnimatedSection delay={0.15}>
                            <Card className="bg-[#7C3AED]/5 border-[#7C3AED]/15 p-6 h-full relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-[60px] bg-[#F59E0B]/10" />
                                <div className="flex items-center gap-2 mb-5 relative">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center">
                                        <Check className="w-4 h-4 text-white" />
                                    </div>
                                    <h3 className="font-bold text-foreground">Avec Homly Partenaires</h3>
                                </div>
                                <ul className="space-y-3.5 relative">
                                    {[
                                        "L'agent immobilier vous recommande directement à son client",
                                        "Le client vous appelle déjà convaincu — zéro mise en concurrence",
                                        "Votre carnet de commandes se remplit naturellement",
                                        "Chaque vente immobilière dans votre zone = un client potentiel",
                                        "Vous vous concentrez sur votre métier, pas sur la prospection",
                                    ].map((t, i) => (
                                        <li key={i} className="flex items-start gap-2.5">
                                            <Check className="w-4 h-4 mt-0.5 text-[#F59E0B] flex-shrink-0" />
                                            <span className="text-sm text-foreground font-medium">{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* ===== HOW IT WORKS ===== */}
            <section className="py-20 md:py-24 bg-card/30">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <Badge className="bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20 mb-4">
                            <Play className="w-3 h-3 mr-1" /> Simple et concret
                        </Badge>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground">
                            Comment ça marche ?
                        </h2>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
                        {/* Connection line */}
                        <div className="hidden md:block absolute top-[52px] left-[17%] right-[17%] h-[2px] bg-gradient-to-r from-[#7C3AED] via-[#F59E0B] to-[#7C3AED] opacity-20" />

                        {[
                            {
                                step: '01', icon: FileCheck, color: 'from-[#7C3AED] to-[#7C3AED]',
                                title: "Créez votre profil", desc: "Inscrivez-vous en 3 minutes. Décrivez votre activité, vos certifications, votre zone. Notre équipe vérifie et active votre profil sous 48h."
                            },
                            {
                                step: '02', icon: Search, color: 'from-[#7C3AED] to-[#F59E0B]',
                                title: "Les agents vous trouvent", desc: "Votre profil apparaît dans l'annuaire Homly, utilisé chaque jour par des centaines d'agents immobiliers. Ils filtrent par métier, zone et avis."
                            },
                            {
                                step: '03', icon: Handshake, color: 'from-[#F59E0B] to-[#F59E0B]',
                                title: "Vous recevez des clients", desc: "L'agent recommande vos services à son client acheteur. Le client vous contacte — déjà en confiance, déjà décidé. Vous n'avez plus qu'à intervenir."
                            },
                        ].map((s, i) => (
                            <AnimatedSection key={i} delay={i * 0.15} className="text-center relative">
                                <div className={`w-[72px] h-[72px] rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center mx-auto mb-5 shadow-lg relative z-10`}>
                                    <s.icon className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-card border-2 border-[#7C3AED]/30 flex items-center justify-center -mt-2 z-20">
                                    <span className="text-[10px] font-bold text-[#F59E0B]">{s.step}</span>
                                </div>
                                <h3 className="font-bold text-foreground text-lg mb-2">{s.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== KEY BENEFITS ===== */}
            <section className="py-20 md:py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-4">
                            Ce que Homly Partenaires change<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">concrètement</span> pour vous
                        </h2>
                    </AnimatedSection>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
                        {[
                            { icon: Target, title: "Clients qualifiés", desc: "Pas de curieux. Le client a acheté un bien et a besoin de travaux. Le taux de conversion est 5x supérieur à un devis classique.", accent: '#F59E0B' },
                            { icon: BadgeCheck, title: "Crédibilité instantanée", desc: "Le badge 'Vérifié Homly' est un gage de qualité. L'agent a déjà fait le tri pour le client. Vous partez avec une longueur d'avance.", accent: '#7C3AED' },
                            { icon: Eye, title: "Visibilité permanente", desc: "Votre profil est visible 24/7 par tous les agents de votre zone. Pas besoin de faire de la pub : les recommandations viennent à vous.", accent: '#F59E0B' },
                            { icon: BarChart3, title: "Statistiques de performance", desc: "Suivez en temps réel vos vues, clics et recommandations. Comprenez ce qui fonctionne et optimisez votre profil.", accent: '#7C3AED' },
                            { icon: Star, title: "Avis qui comptent", desc: "Les avis laissés par des agents immobiliers ont plus de poids qu'un avis Google anonyme. C'est la recommandation d'un professionnel à un autre.", accent: '#F59E0B' },
                            { icon: Clock, title: "Un seul chantier rembourse tout", desc: "Avec un panier moyen de 15 000€ par acheteur, un seul client obtenu via Homly rembourse des années d'abonnement.", accent: '#7C3AED' },
                        ].map((b, i) => (
                            <AnimatedSection key={i} delay={i * 0.08}>
                                <Card className="bg-card border-border p-5 h-full hover:border-[#7C3AED]/30 transition-colors group">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${b.accent}12` }}>
                                        <b.icon className="w-5 h-5" style={{ color: b.accent }} />
                                    </div>
                                    <h3 className="font-bold text-foreground mb-2">{b.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                                </Card>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== TESTIMONIALS ===== */}
            <section className="py-20 md:py-24 bg-card/30">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-16">
                        <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20 mb-4">
                            <MessageSquare className="w-3 h-3 mr-1" /> Premiers retours
                        </Badge>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground">
                            Ce qu'en disent nos<br />premiers bêta-testeurs
                        </h2>
                    </AnimatedSection>

                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {TESTIMONIALS.map((t, i) => (
                            <AnimatedSection key={i} delay={i * 0.12}>
                                <Card className="bg-card border-border p-6 h-full flex flex-col">
                                    {/* Stars */}
                                    <div className="flex gap-0.5 mb-4">
                                        {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                                    </div>
                                    {/* Quote */}
                                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">"{t.text}"</p>
                                    {/* Metric */}
                                    <div className="px-3 py-1.5 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-bold w-fit mb-4">
                                        {t.metric}
                                    </div>
                                    {/* Author */}
                                    <div className="flex items-center gap-3 pt-4 border-t border-border">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center text-white text-sm font-bold">
                                            {t.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-foreground">{t.name}</p>
                                            <p className="text-xs text-muted-foreground">{t.role}</p>
                                        </div>
                                    </div>
                                </Card>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== VISION — CADENAS (FONCTIONNALITÉS À VENIR) ===== */}
            <section className="py-20 md:py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-14">
                        <Badge className="bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20 mb-4">
                            <Lock className="w-3 h-3 mr-1" /> Notre vision
                        </Badge>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-3">
                            Ce que nous construisons<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">pour vous</span>
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Homly Partenaires évolue en continu. Voici les prochaines fonctionnalités sur notre feuille de route. 
                            Les premiers inscrits en bêta en profiteront en priorité.
                        </p>
                    </AnimatedSection>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
                        {[
                            {
                                icon: Users, title: "Accès grand public", 
                                desc: "À terme, les particuliers pourront aussi rechercher des artisans directement sur Homly — sans passer par un agent immobilier. Votre profil sera visible par tous.",
                                status: "Prévu 2027", locked: true, accent: '#F59E0B'
                            },
                            {
                                icon: MessageSquare, title: "Messagerie intégrée",
                                desc: "Échangez directement avec les agents et les clients depuis votre espace Homly Partenaires. Plus besoin de jongler entre emails et appels.",
                                status: "En développement", locked: false, accent: '#7C3AED'
                            },
                            {
                                icon: BarChart3, title: "Tableau de bord avancé",
                                desc: "Statistiques détaillées : nombre de vues, taux de contact, comparaison avec votre zone. Pilotez votre visibilité.",
                                status: "Disponible en bêta", locked: false, accent: '#F59E0B'
                            },
                            {
                                icon: Globe, title: "SEO & Page publique",
                                desc: "Chaque profil vérifié aura sa propre page publique référencée sur Google. Votre réputation Homly devient votre meilleure carte de visite en ligne.",
                                status: "Prévu fin 2026", locked: true, accent: '#7C3AED'
                            },
                            {
                                icon: Star, title: "Avis certifiés multi-sources",
                                desc: "Import automatique de vos avis Google + avis vérifiés Homly. Un score de confiance unique qui combine toutes vos recommandations.",
                                status: "Prévu fin 2026", locked: true, accent: '#F59E0B'
                            },
                            {
                                icon: CircleDollarSign, title: "Devis en ligne intégré",
                                desc: "Le client pourra demander un devis directement depuis votre profil. Vous recevez la demande structurée, prête à traiter.",
                                status: "Prévu 2027", locked: true, accent: '#7C3AED'
                            },
                        ].map((feature, i) => (
                            <AnimatedSection key={i} delay={i * 0.08}>
                                <Card className={`relative overflow-hidden h-full transition-all ${feature.locked ? 'border-border opacity-85' : 'border-[#7C3AED]/20 bg-card'}`}>
                                    {/* Lock overlay for locked features */}
                                    {feature.locked && (
                                        <div className="absolute top-3 right-3 z-10">
                                            <div className="w-7 h-7 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                                                <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${feature.locked ? 'bg-muted' : `bg-gradient-to-br from-[#7C3AED] to-[#F59E0B]`}`}>
                                                <feature.icon className={`w-5 h-5 ${feature.locked ? 'text-muted-foreground' : 'text-white'}`} />
                                            </div>
                                            <div>
                                                <h3 className={`font-bold text-sm ${feature.locked ? 'text-muted-foreground' : 'text-foreground'}`}>{feature.title}</h3>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                                    feature.locked 
                                                        ? 'bg-white/5 text-muted-foreground border border-white/10' 
                                                        : 'bg-green-500/10 text-green-400 border border-green-500/20'
                                                }`}>
                                                    {feature.status}
                                                </span>
                                            </div>
                                        </div>
                                        <p className={`text-sm leading-relaxed ${feature.locked ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                                            {feature.desc}
                                        </p>
                                    </div>
                                </Card>
                            </AnimatedSection>
                        ))}
                    </div>

                    <AnimatedSection className="text-center mt-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/10 text-sm text-muted-foreground">
                            <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                            <span>Les premiers inscrits en bêta auront un <strong className="text-foreground">accès prioritaire</strong> à toutes ces fonctionnalités.</span>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ===== SPECIALTIES ===== */}
            <section className="py-20 md:py-24">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-3">
                            19 métiers représentés
                        </h2>
                        <p className="text-muted-foreground">Quel que soit votre corps de métier, il y a des clients qui vous cherchent.</p>
                    </AnimatedSection>
                    <AnimatedSection>
                        <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
                            {SPECIALTIES_DISPLAY.map((s, i) => (
                                <div key={i} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-card border border-border hover:border-[#7C3AED]/30 transition-colors">
                                    <s.icon className="w-4 h-4 text-[#F59E0B]" />
                                    <span className="text-sm text-foreground whitespace-nowrap">{s.label}</span>
                                </div>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ===== PRICING ===== */}
            <section ref={pricingRef} className="py-20 md:py-24 bg-card/30" id="pricing">
                <div className="max-w-5xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-12">
                        <Badge className="bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20 mb-4">
                            <CircleDollarSign className="w-3 h-3 mr-1" /> Tarifs transparents
                        </Badge>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-3">
                            Un investissement qui se rembourse<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">dès le premier client</span>
                        </h2>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Sans engagement. Résiliable à tout moment. Essayez, mesurez, décidez.
                        </p>
                    </AnimatedSection>

                    {!showForm ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
                                {Object.entries(PLANS).map(([key, plan]) => (
                                    <PricingCard key={key} planKey={key} plan={plan} isSelected={selectedPlan === key} onSelect={setSelectedPlan} />
                                ))}
                            </div>
                            <div className="text-center">
                                <Button onClick={openForm} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white px-8 py-3 text-base font-semibold shadow-lg shadow-[#7C3AED]/20" data-testid="continue-to-form">
                                    Créer mon profil — {PLANS[selectedPlan]?.price}€/mois
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <p className="text-xs text-muted-foreground mt-3">Activation sous 48h après vérification</p>
                            </div>
                        </>
                    ) : (
                        <div ref={formRef} className="max-w-2xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-foreground">
                                    Inscription — {PLANS[selectedPlan]?.name} ({PLANS[selectedPlan]?.price}€/mois)
                                </h2>
                                <Button variant="ghost" onClick={() => setShowForm(false)} className="text-muted-foreground">Changer de forfait</Button>
                            </div>
                            <RegistrationForm selectedPlan={selectedPlan} specialties={specialties} onSubmit={handleSubmit} submitting={submitting} />
                        </div>
                    )}
                </div>
            </section>

            {/* ===== FAQ ===== */}
            <section className="py-20 md:py-24">
                <div className="max-w-3xl mx-auto px-6">
                    <AnimatedSection className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-3">
                            Vos questions, nos réponses
                        </h2>
                        <p className="text-muted-foreground">Tout ce que vous devez savoir avant de vous lancer.</p>
                    </AnimatedSection>

                    <div className="space-y-3">
                        {FAQ.map((item, i) => (
                            <AnimatedSection key={i} delay={i * 0.05}>
                                <Card className="bg-card border-border overflow-hidden">
                                    <button
                                        onClick={() => setShowFaq(showFaq === i ? null : i)}
                                        className="w-full p-5 flex items-center justify-between text-left gap-4"
                                        data-testid={`faq-${i}`}
                                    >
                                        <span className="font-semibold text-foreground text-sm">{item.q}</span>
                                        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${showFaq === i ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {showFaq === i && (
                                            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                                <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Card>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== FINAL CTA ===== */}
            <section className="py-20 md:py-24 relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[200px] bg-[#7C3AED]/8" />
                </div>
                <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-4">
                            Rejoignez la bêta et<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7C3AED] to-[#F59E0B]">prenez une longueur d'avance</span>
                        </h2>
                        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                            Les premières places sont limitées. Inscrivez-vous maintenant pour faire partie 
                            des artisans qui seront déjà visibles quand le réseau s'ouvrira au grand public.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button onClick={scrollToPricing} className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] text-white px-8 py-3 text-base font-semibold shadow-lg shadow-[#7C3AED]/20" data-testid="final-cta">
                                Créer mon profil gratuitement
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Link to="/pro/login" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                                Déjà membre ? Se connecter <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* ===== FOOTER ===== */}
            <footer className="border-t border-border py-8">
                <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <HomlyLogo size="sm" />
                    <p className="text-xs text-muted-foreground">© 2025 Homly. Tous droits réservés.</p>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                        <Link to="/cgu" className="hover:text-foreground">CGU</Link>
                        <Link to="/privacy" className="hover:text-foreground">RGPD</Link>
                        <Link to="/mentions-legales" className="hover:text-foreground">Mentions légales</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
